<template>
  <div class="app-container openapi-monitor">
    <el-card class="box-card">
      <div class="page-title">开放接口调用监控</div>

      <div class="filter-container">
        <el-form :model="query" :inline="true">
          <el-form-item label="时间范围：">
            <el-date-picker v-model="range" type="datetimerange" size="small"
                            value-format="yyyy-MM-dd HH:mm:ss"
                            start-placeholder="开始时间" end-placeholder="结束时间"
                            :default-time="['00:00:00', '23:59:59']" />
          </el-form-item>
          <el-form-item label="接入方：">
            <el-select v-model="query.apiKeyId" placeholder="全部接入方" clearable size="small" style="width: 180px">
              <el-option v-for="k in keyOptions" :key="k.id" :label="k.appName" :value="k.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="接口：">
            <el-input v-model="query.path" placeholder="接口路径，精确匹配" clearable size="small" style="width: 220px" />
          </el-form-item>
          <el-form-item label="拒绝原因：">
            <el-select v-model="query.denyReason" clearable size="small" placeholder="全部">
              <el-option label="接口越权" value="API_FORBIDDEN" />
              <el-option label="路由/方法未开放" value="ROUTE_FORBIDDEN" />
              <el-option label="IP 不符" value="IP_FORBIDDEN" />
              <el-option label="无 Key" value="NO_KEY" />
              <el-option label="Key 无效/停用" value="INVALID_KEY" />
              <el-option label="限流" value="RATE_LIMITED" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" size="mini" @click="handleQuery">查询</el-button>
            <el-button type="info" size="mini" plain @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
      </div>

      <el-tabs v-model="activeTab" @tab-click="handleTabChange">
        <!-- ── 总览 ─────────────────────────────────── -->
        <el-tab-pane label="总览（按天）" name="daily">
          <el-table v-loading="loading.daily" :data="daily" size="small">
            <el-table-column label="日期" prop="day" align="center" width="130" />
            <el-table-column label="调用量" prop="callCount" align="center" width="100" />
            <el-table-column label="成功" prop="successCount" align="center" width="90" />
            <el-table-column label="失败" align="center" width="90">
              <template slot-scope="{row}">
                <span :class="{ 'text-danger': row.failCount > 0 }">{{ row.failCount }}</span>
              </template>
            </el-table-column>
            <el-table-column label="成功率" align="center" width="110">
              <template slot-scope="{row}">
                <span :class="rateClass(row.successRate)">{{ row.successRate }}%</span>
              </template>
            </el-table-column>
            <el-table-column label="平均耗时" align="center" width="110">
              <template slot-scope="{row}">{{ row.avgCostMs }} ms</template>
            </el-table-column>
            <el-table-column label="最大耗时" align="center" width="110">
              <template slot-scope="{row}">{{ row.maxCostMs }} ms</template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <!-- ── 按接入方 ─────────────────────────────── -->
        <el-tab-pane label="按接入方" name="byApp">
          <el-table v-loading="loading.byApp" :data="byApp" size="small">
            <el-table-column label="接入方" align="left" min-width="200">
              <template slot-scope="{row}">
                <!--
                  ⚠️ groupKey 为空 = 这一组是**没通过鉴权**的调用（无 Key / 错 Key / IP 不在白名单），
                     不是"数据缺失"。必须显示成可读的文案，它恰恰是这页最该点开看的一行。
                -->
                <span v-if="row.groupKey">{{ row.groupKey }}</span>
                <el-tag v-else size="mini" type="danger">未通过鉴权</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="调用次数" prop="callCount" align="center" width="110" />
            <el-table-column label="失败次数" align="center" width="110">
              <template slot-scope="{row}">
                <span :class="{ 'text-danger': row.failCount > 0 }">{{ row.failCount }}</span>
              </template>
            </el-table-column>
            <el-table-column label="成功率" align="center" width="110">
              <template slot-scope="{row}">
                <span :class="rateClass(row.successRate)">{{ row.successRate }}%</span>
              </template>
            </el-table-column>
            <el-table-column label="平均耗时" align="center" width="110">
              <template slot-scope="{row}">{{ row.avgCostMs }} ms</template>
            </el-table-column>
            <el-table-column label="最大耗时" align="center" width="110">
              <template slot-scope="{row}">{{ row.maxCostMs }} ms</template>
            </el-table-column>
            <el-table-column label="最近调用" prop="lastCalledAt" align="center" width="170" />
          </el-table>
        </el-tab-pane>

        <!-- ── 按接口 ───────────────────────────────── -->
        <el-tab-pane label="按接口" name="byPath">
          <div class="tip-box mb10">
            <i class="el-icon-info"></i>
            这里会出现<b>系统里并不存在的路径</b> —— 那是接入方在猜接口名做试探，
            全部是 4xx。它们不是脏数据，是要看的东西。
          </div>
          <el-table v-loading="loading.byPath" :data="byPath" size="small">
            <el-table-column label="接口路径" prop="groupKey" align="left" min-width="280" show-overflow-tooltip />
            <el-table-column label="调用次数" prop="callCount" align="center" width="110" />
            <el-table-column label="失败次数" align="center" width="110">
              <template slot-scope="{row}">
                <span :class="{ 'text-danger': row.failCount > 0 }">{{ row.failCount }}</span>
              </template>
            </el-table-column>
            <el-table-column label="成功率" align="center" width="110">
              <template slot-scope="{row}">
                <span :class="rateClass(row.successRate)">{{ row.successRate }}%</span>
              </template>
            </el-table-column>
            <el-table-column label="平均耗时" align="center" width="110">
              <template slot-scope="{row}">{{ row.avgCostMs }} ms</template>
            </el-table-column>
            <el-table-column label="最近调用" prop="lastCalledAt" align="center" width="170" />
          </el-table>
        </el-tab-pane>

        <!-- ── 调用明细 ─────────────────────────────── -->
        <el-tab-pane label="调用明细" name="logs">
          <div class="btn-box mb10">
            <el-checkbox v-model="query.onlyFailed" @change="doQueryLogs({ pageNo: 1 })">
              只看失败（含无 Key / 错 Key / IP 不符）
            </el-checkbox>
            <el-checkbox v-model="query.onlyNoKey" class="ml20" @change="doQueryLogs({ pageNo: 1 })">
              只看未识别 Key
            </el-checkbox>
            <el-input v-model="query.clientIp" placeholder="来源 IP" clearable size="mini"
                      class="ml20 inline-input" @clear="doQueryLogs({ pageNo: 1 })" />
            <el-input v-model="query.httpStatus" placeholder="状态码" clearable size="mini"
                      class="ml10 inline-input-sm" @clear="doQueryLogs({ pageNo: 1 })" />
            <el-button type="primary" size="mini" class="ml10" @click="doQueryLogs({ pageNo: 1 })">筛选</el-button>
          </div>
          <el-table v-loading="loading.logs" :data="logs" size="small">
            <el-table-column label="调用时间" prop="createdTime" align="center" width="170" />
            <el-table-column label="接入方" align="center" width="150">
              <template slot-scope="{row}">
                <span v-if="row.appName">{{ row.appName }}</span>
                <el-tag v-else size="mini" type="danger">未通过鉴权</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="接口路径" prop="path" align="left" min-width="240" show-overflow-tooltip />
            <el-table-column label="查询串" prop="queryString" align="left" min-width="200" show-overflow-tooltip>
              <template slot-scope="{row}">{{ row.queryString || '—' }}</template>
            </el-table-column>
            <el-table-column label="方法" prop="requestMethod" width="80" />
            <el-table-column label="拒绝原因" prop="denyReason" min-width="170" />
            <el-table-column label="来源 IP" prop="clientIp" align="center" width="140" />
            <el-table-column label="状态码" align="center" width="90">
              <template slot-scope="{row}">
                <el-tag size="mini" :type="row.httpStatus >= 400 ? 'danger' : 'success'">
                  {{ row.httpStatus }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="返回条数" align="center" width="100">
              <template slot-scope="{row}">
                {{ row.resultCount === null || row.resultCount === undefined ? '—' : row.resultCount }}
              </template>
            </el-table-column>
            <el-table-column label="耗时" align="center" width="100">
              <template slot-scope="{row}">{{ row.costMs }} ms</template>
            </el-table-column>
          </el-table>
          <pagination v-show="logTotal > 0" :total="logTotal"
                      :pageNo.sync="query.pageNo" :pageSize.sync="query.pageSize"
                      @pagination="doQueryLogs" />
        </el-tab-pane>
      </el-tabs>
    </el-card>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination'
import openApiManageApi from '@/api/openapi'

function initQuery() {
  return {
    pageNo: 1,
    pageSize: 20,
    from: undefined,
    to: undefined,
    apiKeyId: undefined,
    path: undefined,
    httpStatus: undefined,
    denyReason: undefined,
    clientIp: undefined,
    onlyFailed: false,
    onlyNoKey: false
  }
}

export default {
  name: 'OpenapiMonitor',
  components: { Pagination },
  data() {
    return {
      activeTab: 'daily',
      range: [],
      query: initQuery(),
      keyOptions: [],
      daily: [],
      byApp: [],
      byPath: [],
      logs: [],
      logTotal: 0,
      loading: { daily: false, byApp: false, byPath: false, logs: false }
    }
  },
  created() {
    this.loadKeyOptions()
    this.doQueryDaily()
  },
  methods: {
    loadKeyOptions() {
      return openApiManageApi.keyList().then(data => {
        this.keyOptions = (data && data.data) || []
      })
    },
    syncRange() {
      // 后端 from/to 是 LocalDateTime，直接传 'yyyy-MM-dd HH:mm:ss'
      this.query.from = this.range && this.range.length ? this.range[0] : undefined
      this.query.to = this.range && this.range.length ? this.range[1] : undefined
    },
    handleQuery() {
      this.syncRange()
      this.refreshActive({ pageNo: 1 })
    },
    handleReset() {
      this.range = []
      this.query = initQuery()
      this.refreshActive({ pageNo: 1 })
    },
    handleTabChange() {
      // 切页签时按当前筛选条件取数，不用等人再点一次查询
      this.refreshActive({ pageNo: 1 })
    },
    refreshActive({ pageNo } = {}) {
      if (this.activeTab === 'daily') return this.doQueryDaily()
      if (this.activeTab === 'byApp') return this.doQueryByApp()
      if (this.activeTab === 'byPath') return this.doQueryByPath()
      return this.doQueryLogs({ pageNo: pageNo || 1 })
    },
    statParams() {
      // 统计三个页签不需要分页参数，但共用同一套筛选条件 ——
      // 和明细用同一个 query 对象，保证"总览看到的数"和"点进去筛出来的明细"是同一批
      const { from, to, apiKeyId, path, httpStatus, clientIp, onlyFailed, onlyNoKey, denyReason } = this.query
      return { from, to, apiKeyId, path, httpStatus, clientIp, onlyFailed, onlyNoKey, denyReason }
    },
    doQueryDaily() {
      this.loading.daily = true
      return openApiManageApi.statDaily(this.statParams())
        .then(data => { this.daily = (data && data.data) || [] })
        .finally(() => { this.loading.daily = false })
    },
    doQueryByApp() {
      this.loading.byApp = true
      return openApiManageApi.statByApp(this.statParams())
        .then(data => { this.byApp = (data && data.data) || [] })
        .finally(() => { this.loading.byApp = false })
    },
    doQueryByPath() {
      this.loading.byPath = true
      return openApiManageApi.statByPath(this.statParams())
        .then(data => { this.byPath = (data && data.data) || [] })
        .finally(() => { this.loading.byPath = false })
    },
    doQueryLogs({ pageNo, pageSize } = {}) {
      if (pageNo) this.query.pageNo = pageNo
      if (pageSize) this.query.pageSize = pageSize
      this.syncRange()
      this.loading.logs = true
      return openApiManageApi.logList(this.query)
        .then(data => {
          this.logs = (data.data && data.data.list) || []
          this.logTotal = (data.data && data.data.total) || 0
        })
        .finally(() => { this.loading.logs = false })
    },
    rateClass(rate) {
      if (rate === null || rate === undefined) return ''
      return Number(rate) < 100 ? 'text-danger' : ''
    }
  }
}
</script>

<style lang="scss" scoped>
.openapi-monitor {
  .page-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 12px;
  }
  .tip-box {
    font-size: 13px;
    color: #909399;
    line-height: 20px;
  }
  .text-danger {
    color: #f56c6c;
  }
  .ml10 { margin-left: 10px; }
  .ml20 { margin-left: 20px; }
  .inline-input { width: 150px; }
  .inline-input-sm { width: 100px; }
}
</style>
